import * as XLSX from 'xlsx';

/**
 * Utility to export report data to Excel.
 * @param {string} reportName - The name of the report being exported.
 * @param {Object} data - The JSON data associated with the report.
 */
export const exportReportToExcel = (reportName, data) => {
    if (!data) {
        console.error("No data provided for Excel export");
        return;
    }

    const workbook = XLSX.utils.book_new();
    let worksheet;
    let fileName = `${reportName.replace(/\//g, '-')}_${new Date().toISOString().split('T')[0]}.xlsx`;

    const formatHour = (h) => {
        return h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
    };

    const formatDay = (d) => {
        const days = ["", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        return days[d] || d;
    };

    switch (reportName) {
        case "Usage Frequency":
            const trendData = data.trends?.map(t => ({
                "Date/Period": t._id,
                "Session Count": t.count
            })) || [];
            worksheet = XLSX.utils.json_to_sheet(trendData);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Trends");
            if (data.metrics) {
                const metricsSheet = XLSX.utils.json_to_sheet(
                    Object.entries(data.metrics).map(([key, value]) => ({ "Metric": key, "Value": value }))
                );
                XLSX.utils.book_append_sheet(workbook, metricsSheet, "Summary Metrics");
            }
            break;

        case "Time of Day / Day of Week Usage":
            const hourlyData = data.hourly?.map(h => ({
                "Hour": formatHour(h._id),
                "Session Count": h.count
            })) || [];
            const hourlySheet = XLSX.utils.json_to_sheet(hourlyData);
            XLSX.utils.book_append_sheet(workbook, hourlySheet, "Hourly Breakdown");
            
            const dailyData = data.daily?.map(d => ({
                "Day of Week": formatDay(d._id),
                "Session Count": d.count
            })) || [];
            const dailySheet = XLSX.utils.json_to_sheet(dailyData);
            XLSX.utils.book_append_sheet(workbook, dailySheet, "Daily Breakdown");
            break;

        case "Daily Utilization Report":
            const utilData = data.utilization?.map(u => ({
                "Date": u.date,
                "Booked Sessions": u.booked,
                "Empty Slots": u.empty,
                "Total Capacity": u.total,
                "Occupancy %": `${u.percentage}%`
            })) || [];
            worksheet = XLSX.utils.json_to_sheet(utilData);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Utilization");
            break;

        case "Idle Time Reporting":
            const formatMinsToTime = (m) => {
                const h = Math.floor(m / 60);
                const mm = m % 60;
                const ampm = h >= 12 ? 'PM' : 'AM';
                const hh = h % 12 || 12;
                return `${hh}:${mm.toString().padStart(2, '0')} ${ampm}`;
            };

            const domeStats = data.domes?.map(d => ({
                "Dome Name": d.domeName,
                "Total Idle (Mins)": d.totalIdleMinutes,
                "Total Idle (Hours)": (d.totalIdleMinutes / 60).toFixed(1),
                "Number of Sessions": d.timeline?.filter(t => t.type === 'session').length || 0
            })) || [];
            worksheet = XLSX.utils.json_to_sheet(domeStats);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Dome Summary");

            const allSessions = [];
            data.domes?.forEach(d => {
                d.timeline?.filter(t => t.type === 'session').forEach(s => {
                    allSessions.push({
                        "Dome": d.domeName,
                        "Session Name": s.name,
                        "Start Time": formatMinsToTime(s.start),
                        "End Time": formatMinsToTime(s.end),
                        "Duration (Mins)": s.duration
                    });
                });
            });
            if (allSessions.length > 0) {
                const sessionSheet = XLSX.utils.json_to_sheet(allSessions);
                XLSX.utils.book_append_sheet(workbook, sessionSheet, "Session Details");
            }
            break;

        case "Peak Usage Times":
            const peakData = data.peaks?.map(p => ({
                "Hour": formatHour(p._id),
                "Session Count": p.count
            })) || [];
            worksheet = XLSX.utils.json_to_sheet(peakData);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Peak Times");
            break;

        case "Session Growth Rate Report":
            const monthlyData = data.monthly?.map(m => ({
                "Month": m._id,
                "Session Count": m.count
            })) || [];
            worksheet = XLSX.utils.json_to_sheet(monthlyData);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Growth");
            break;

        case "Weekend vs Weekday Usage":
            const weekendData = data.stats?.map(s => ({
                "Type": s._id ? "Weekend" : "Weekday",
                "Avg Sessions": s.avgCount,
                "Avg Duration (Mins)": s.avgDuration
            })) || [];
            worksheet = XLSX.utils.json_to_sheet(weekendData);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Weekend vs Weekday");
            break;

        case "Session Density":
            const densityData = data.density?.map(item => ({
                "Day": formatDay(item._id.day),
                "Hour": formatHour(item._id.hour),
                "Session Count": item.count
            })) || [];
            worksheet = XLSX.utils.json_to_sheet(densityData);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Density Map");
            break;

        case "Usage Saturation Report":
            const saturationData = data.details?.map(item => ({
                "Date": item.date,
                "Dome": item.dome,
                "Total Slots": item.capacity,
                "Booked Slots": item.booked,
                "Saturation %": `${item.percentage}%`,
                "Status": item.bucket
            })) || [];
            worksheet = XLSX.utils.json_to_sheet(saturationData);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Daily Saturation");
            if (data.summary) {
                const summarySheet = XLSX.utils.json_to_sheet(
                    Object.entries(data.summary).map(([key, value]) => ({ "Occupancy Level": key, "Day Count": value }))
                );
                XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary Bucket");
            }
            break;

        case "High vs Low Demand Days / Weeks / Months":
            const highData = data.highDemand?.map(d => ({
                "Date": d._id,
                "Session Count": d.count
            })) || [];
            const highSheet = XLSX.utils.json_to_sheet(highData);
            XLSX.utils.book_append_sheet(workbook, highSheet, "High Demand");

            const lowData = data.lowDemand?.map(d => ({
                "Date": d._id,
                "Session Count": d.count
            })) || [];
            const lowSheet = XLSX.utils.json_to_sheet(lowData);
            XLSX.utils.book_append_sheet(workbook, lowSheet, "Low Demand");
            break;

        case "Session Succession Report":
            const successionData = data.events?.map(ev => ({
                "Dome": ev.dome,
                "Date": ev.date,
                "Session 1": ev.firstSession,
                "Session 2": ev.secondSession,
                "Gap (Mins)": ev.gap
            })) || [];
            worksheet = XLSX.utils.json_to_sheet(successionData);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Back-to-Back Sessions");
            if (data.metrics) {
                const successionMetrics = XLSX.utils.json_to_sheet([
                    { "Metric": "Succession Rate", "Value": data.metrics["Succession Rate"] },
                    { "Metric": "Back-to-Back Sessions", "Value": data.metrics["Back-to-Back Sessions"] }
                ]);
                XLSX.utils.book_append_sheet(workbook, successionMetrics, "Metrics");
            }
            break;

        default:
            worksheet = XLSX.utils.json_to_sheet(Array.isArray(data) ? data : [data]);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
            break;
    }

    XLSX.writeFile(workbook, fileName);
};
